import axios from 'axios';
import { PricingConfig } from 'hooks/billing/usePricingConfig';
import { StatusCodes } from 'http-status-codes';
import {
  GetBillingInfoResponse,
  OVERAGE_SOURCE,
  OverageInfo,
  SUBSCRIPTION_STATE,
} from 'network/httpClients/BillingClient/api.schema';
import { ACCOUNT_TIERS, AccountInfoResponse } from 'types/billing';
import {
  isMigratedFreeUser,
  isMigratedPaidUser,
  showsReversedMigrationOffer,
} from 'utils/billing/conversion';
import { formatUserFriendlyDate } from 'utils/date';
import { formatCurrency, formatNumber } from 'utils/number';
import { spawn } from 'utils/translations';
import { DEFAULT_DATE_RANGE_DAYS } from './consts';
import { BonusTier, TIER_DISCOUNT, isBonusTier } from './parts/consts';
import type {
  BillingDateRange,
  BillingPortalErrorResolution,
  CurrentPlanDisplay,
  CurrentPlanDisplayOptions,
} from './types';

export {
  formatChartLabel,
  formatXAxisLabel,
  rangeToIsoParams,
  getComplexityLabel,
} from '../../utils/billing';

/**
 * Computes the default date range for the billing page.
 * @returns A BillingDateRange spanning the past DEFAULT_DATE_RANGE_DAYS days up to today.
 */
export const computeDefaultRange = (): BillingDateRange => {
  const to = new Date();
  const from = new Date(to);

  from.setDate(from.getDate() - (DEFAULT_DATE_RANGE_DAYS - 1));

  return { from, to };
};

/**
 * Resolves the monthly USD price of the plan whose pool matches the granted
 * credits, by looking it up in the pricing catalog.
 * @param creditsGranted - The plan's granted monthly credits, if known.
 * @param pricingConfig - The pricing catalog of available plan SKUs.
 * @returns The monthly price in dollars, or undefined when no matching SKU exists.
 */
export const getPlanMonthlyPrice = (
  creditsGranted: number | undefined,
  pricingConfig: PricingConfig,
): number | undefined => {
  if (!creditsGranted) {
    return;
  }

  return pricingConfig.plans.find((plan) => plan.credits === creditsGranted)
    ?.priceMonthly;
};

/**
 * Formats the granted monthly credits for display.
 * @param creditsGranted - The plan's granted monthly credits, if known.
 * @returns The comma-formatted credit count, or undefined when no plan is active.
 */
export const getPlanCreditsDisplay = (
  creditsGranted: number | undefined,
): string | undefined =>
  !creditsGranted ? undefined : formatNumber(creditsGranted);

/**
 * Formats the plan's monthly price for display.
 * @param creditsGranted - The plan's granted monthly credits, if known.
 * @param pricingConfig - The pricing catalog of available plan SKUs.
 * @returns The formatted price (e.g. "$30"), or undefined when no matching SKU exists.
 */
export const getPlanPriceDisplay = (
  creditsGranted: number | undefined,
  pricingConfig: PricingConfig,
): string | undefined => {
  const priceMonthly = getPlanMonthlyPrice(creditsGranted, pricingConfig);

  return !priceMonthly ? undefined : formatCurrency(priceMonthly);
};

/**
 * Builds the overage-cap row description from the server overage block: the USD
 * ceiling and its credit equivalent (e.g. "$60 (5,000 credits)"), or a
 * "not configured" label when no cap source is set.
 * @param overage - The overage block from GET /billing-info.
 * @returns The formatted cap description, or the not-configured label.
 */
export const getOverageCapDescription = (overage: OverageInfo): string => {
  const spawnManageSubscription = spawn('billing.manage-subscription');

  if (overage.source === OVERAGE_SOURCE.NONE) {
    return spawnManageSubscription('overage-cap.not-configured');
  }

  return spawnManageSubscription('overage-cap.value', {
    price: formatCurrency(overage.chargeLimit),
    credits: formatNumber(overage.skuAmount),
  });
};

/**
 * Builds an interpolated description, returning undefined when any interpolated
 * value is missing so the caller can omit the description entirely rather than
 * rendering partial text like "1,000,000 credits per month - Not available/mo".
 * @param values - The values interpolated into the description.
 * @param buildDescription - Builds the templated description; only called when every value is present.
 * @returns The templated description, or undefined when any value is missing.
 */
export const buildDescriptionIfAvailable = (
  values: Array<string | undefined>,
  buildDescription: () => string,
): string | undefined =>
  values.some((value) => value === undefined) ? undefined : buildDescription();

/**
 * Builds the display model for the current-plan row from the live billing info.
 * Plan name and price come from the account's real Stripe subscription; the
 * credit count comes from the quota balance. The model is gated on
 * subscriptionState (not field truthiness): 'none' yields the no-plan state,
 * 'canceled' keeps the live plan data but flags the row so a "Canceled" badge is
 * shown and swaps the action to the fresh-purchase CTA (the plan is gone, there
 * is nothing left to change), 'pastDue' likewise keeps the live plan data but
 * flags a payment-failure badge (dunning grace: payment failed, plan still
 * live), and 'scheduledToCancel' adds the contractual end date.
 *
 * A lapsed trial also yields the no-plan state regardless of the reported state:
 * the Stripe trial shell is still `trialing`, which the BE maps to 'active', but
 * the account never purchased anything.
 * @param billingInfo - The live billing info from GET /billing-info.
 * @param creditsGranted - The plan's granted monthly credits from /quota, if known.
 * @param options - Scheduled-downgrade credits and the lapsed-trial override.
 * @returns The title, description, optional description note, badges, and action label to render.
 */
export const getCurrentPlanDisplay = (
  billingInfo: GetBillingInfoResponse,
  creditsGranted: number | undefined,
  {
    scheduledChangeCredits,
    isTrialLapsed = false,
  }: CurrentPlanDisplayOptions = {},
): CurrentPlanDisplay => {
  const spawnManageSubscription = spawn('billing.manage-subscription');
  const hasNoActivePlan =
    billingInfo.subscriptionState === SUBSCRIPTION_STATE.NONE || isTrialLapsed;

  if (hasNoActivePlan) {
    return {
      title: spawnManageSubscription('current-plan.empty-title'),
      actionLabel: spawnManageSubscription('current-plan.action-empty'),
    };
  }

  const planCredits = getPlanCreditsDisplay(creditsGranted);
  const planPrice =
    billingInfo.monthlyUsd === null
      ? undefined
      : formatCurrency(billingInfo.monthlyUsd);

  const description = buildDescriptionIfAvailable(
    [planCredits, planPrice],
    () =>
      spawnManageSubscription('current-plan.description', {
        credits: planCredits,
        price: planPrice,
      }),
  );

  const isCanceled =
    billingInfo.subscriptionState === SUBSCRIPTION_STATE.CANCELED;

  const isPaymentFailure =
    billingInfo.subscriptionState === SUBSCRIPTION_STATE.PAST_DUE;

  const isScheduledToCancel =
    billingInfo.subscriptionState === SUBSCRIPTION_STATE.SCHEDULED_TO_CANCEL;

  const scheduledToCancelBadge =
    isScheduledToCancel && billingInfo.cancelAt
      ? spawnManageSubscription('current-plan.scheduled-to-cancel-badge', {
          date: formatUserFriendlyDate(billingInfo.cancelAt),
        })
      : undefined;

  // Scheduled downgrade (PLT-4468): the user keeps the current pool until
  // effectiveAt, then drops to the smaller pack. Mirrors the scheduled-to-cancel
  // path — a warning badge + an ends-on-style note keyed off scheduledChange.
  const scheduledChange =
    billingInfo.subscriptionState === SUBSCRIPTION_STATE.SCHEDULED_TO_CHANGE
      ? billingInfo.scheduledChange
      : undefined;

  const scheduledToChangeBadge = scheduledChange
    ? spawnManageSubscription('current-plan.scheduled-to-change-badge', {
        date: formatUserFriendlyDate(scheduledChange.effectiveAt),
      })
    : undefined;

  const scheduledChangeNote =
    scheduledChange && scheduledChangeCredits !== undefined
      ? spawnManageSubscription('current-plan.scheduled-to-change-note', {
          credits: formatNumber(scheduledChangeCredits),
        })
      : undefined;

  const planName =
    billingInfo.planName ?? spawnManageSubscription('unavailable');

  const descriptionNote = isCanceled ? undefined : scheduledChangeNote;

  // A canceled subscription is terminal, so the only move left is buying a new
  // pack — the same CTA the no-plan state offers, not "Change plan".
  const actionLabel = isCanceled
    ? spawnManageSubscription('current-plan.action-empty')
    : spawnManageSubscription('current-plan.action');

  return {
    title: spawnManageSubscription('current-plan.title', { plan: planName }),
    description,
    descriptionNote,
    isCanceled,
    isPaymentFailure,
    scheduledToCancelBadge,
    scheduledToChangeBadge,
    actionLabel,
  };
};

/**
 * Maps a failed billing-portal request to the user-facing reaction. A 409 means
 * the account is still on trial / has no paid plan, so the user is routed to the
 * plans page to convert; every other status resolves to a toast message.
 * @param error - The error thrown by the billing-portal mutation.
 * @returns Whether to redirect to the plans page, and the toast message otherwise.
 */
export const resolveBillingPortalError = (
  error: unknown,
): BillingPortalErrorResolution => {
  const spawnManageSubscription = spawn('billing.manage-subscription');
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;

  switch (status) {
    case StatusCodes.CONFLICT:
      return { redirectToPlans: true };
    case StatusCodes.FORBIDDEN:
      return {
        redirectToPlans: false,
        toastMessage: spawnManageSubscription('manage-billing.error.not-admin'),
      };
    case StatusCodes.NOT_FOUND:
      return {
        redirectToPlans: false,
        toastMessage: spawnManageSubscription('manage-billing.error.not-setup'),
      };
    default:
      return {
        redirectToPlans: false,
        toastMessage: spawnManageSubscription('manage-billing.error.generic'),
      };
  }
};

/**
 * Resolves the trial discount percentage shown to the user, mirroring the logic
 * rendered by `TrialBonusAlert`: only during an active trial, the migration perk's
 * `percentOff` for migrated free users, otherwise the tier-based fallback
 * (`TIER_DISCOUNT`).
 *
 * @param accountInfo - The account info response, if present.
 * @returns The discount percentage, or `undefined` when no discount applies.
 */
export const getTrialDiscountPct = (
  accountInfo: AccountInfoResponse | undefined,
): number | undefined => {
  const tier = accountInfo?.tier;

  // Migrated free users use the migration perk during an active trial.
  if (isMigratedFreeUser(accountInfo)) {
    if (accountInfo?.trial?.active !== true) return undefined;

    const migrationPerk = accountInfo?.migrationPerk;
    const usesPerk =
      migrationPerk?.active === true && tier !== ACCOUNT_TIERS.paid;

    return usesPerk ? migrationPerk.percentOff : undefined;
  }

  // Migrated paid users use the migration perk (no trial required).
  if (isMigratedPaidUser(accountInfo)) {
    const migrationPerk = accountInfo?.migrationPerk;

    return migrationPerk?.active === true
      ? migrationPerk.percentOff
      : undefined;
  }

  // Reversed (post-cancel) users still within the coupon window: the discount is
  // date-driven and still applies at checkout, so surface the live perk %.
  if (showsReversedMigrationOffer(accountInfo)) {
    return accountInfo?.migrationPerk?.percentOff;
  }

  // Native trial users: discount is only surfaced during an active trial.
  if (accountInfo?.trial?.active !== true) return undefined;

  return tier && isBonusTier(tier)
    ? TIER_DISCOUNT[tier as BonusTier]
    : undefined;
};
